import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import type { OrdreStatus } from "types/ordre";
import { STATUS_CHIP_STYLES, getStatusColor } from "./dropdown/OrdreStatusDropdown";
import { ordresApi } from "@services/api/ordres";
import { useOrdreStore, getOrdreById } from "@store/ordreStore";
import { usePlanningStore } from "@store/planningStore";
import { useAtelierStore } from "@store/atelierStore";
import { useDataStore } from "@store/user_org";
import { isOwnerOrAdmin } from "@utils/permissions";
import ConfirmationModal from "@components/support/ConfirmationModal";
import { Switch } from "@components/ui/selection/Switch";
import { serviceboxProxy } from "@services/serviceboxProxy";
import Icon1 from "@assets/icons/Icon";
import { showNotification } from "context/notification.service";

const STATUS_GROUPS = [
  { label: "Préparation", statuses: ["rdv", "a_preparer", "devis_initial", "commande", "pret"] },
  { label: "Travaux", statuses: ["reception", "en_cours", "devis_supp", "accord", "acompte"] },
  { label: "Clôture", statuses: ["fin_de_travaux", "facturation", "solde", "solde_paye", "disponible", "restitue", "arrete"] },
];

const STATUS_OPTIONS: { value: OrdreStatus; label: string }[] = [
  { value: "a_preparer", label: "À préparer" },
  { value: "devis_initial", label: "Devis initial" },
  { value: "commande", label: "Commandé" },
  { value: "pret", label: "Prêt" },
  { value: "reception", label: "Réceptionné" },
  { value: "en_cours", label: "En cours" },
  { value: "devis_supp", label: "Devis supp." },
  { value: "accord", label: "Accord" },
  { value: "acompte", label: "Acompte" },
  { value: "fin_de_travaux", label: "Fin de travaux" },
  { value: "facturation", label: "Facturation" },
  { value: "solde", label: "Solde" },
  { value: "solde_paye", label: "Solde payé" },
  { value: "disponible", label: "Disponible" },
  { value: "restitue", label: "Restitué" },
  { value: "arrete", label: "Arrêté" },
];

export interface ContextMenuState {
  x: number;
  y: number;
  ordreId: string;
  currentStatus: OrdreStatus;
  num?: string; // ordre number, for the delete confirmation title
}

export function PlanningContextMenu({
  menu,
  onClose,
}: {
  menu: ContextMenuState;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [savingStatus, setSavingStatus] = useState<OrdreStatus | null>(null);

  const userData = useDataStore((s) => s.userData);
  const canDelete = isOwnerOrAdmin(userData);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // While the delete confirmation is open, the menu must NOT auto-close on
  // outside click / scroll / escape (the confirm dialog lives outside the menu).
  const deleteOpenRef = useRef(false);
  useEffect(() => {
    deleteOpenRef.current = showDeleteConfirm;
  }, [showDeleteConfirm]);

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await ordresApi.delete(menu.ordreId);
      const planningState = usePlanningStore.getState();
      if (planningState._fetchedOrgId) {
        planningState.fetchAppointments(planningState._fetchedOrgId, true);
      }
      const atelierState = useAtelierStore.getState();
      if (atelierState._fetchedOrgId) {
        atelierState.fetchData(atelierState._fetchedOrgId, true);
      }
      showNotification({ type: "success", message: "Ordre supprimé" });
      setShowDeleteConfirm(false);
      onClose();
    } catch {
      showNotification({ type: "error", message: "Erreur lors de la suppression" });
      setDeleting(false);
    }
  };

  // Position the menu so it doesn't overflow the viewport
  const [pos, setPos] = useState({ top: menu.y, left: menu.x });
  useEffect(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    let top = menu.y;
    let left = menu.x;
    if (top + rect.height > window.innerHeight - 8) {
      top = window.innerHeight - rect.height - 8;
    }
    if (left + rect.width > window.innerWidth - 8) {
      left = window.innerWidth - rect.width - 8;
    }
    setPos({ top: Math.max(8, top), left: Math.max(8, left) });
  }, [menu.x, menu.y]);

  // Close on outside click or Escape
  const handleClose = useCallback((e: MouseEvent) => {
    if (deleteOpenRef.current) return; // confirm dialog open — keep the menu mounted
    if (ref.current && !ref.current.contains(e.target as Node)) onClose();
  }, [onClose]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (deleteOpenRef.current) { setShowDeleteConfirm(false); return; }
      onClose();
    };
    document.addEventListener("mousedown", handleClose);
    document.addEventListener("keydown", handleKey);
    // Also close on scroll (any scrollable parent) — but not while confirming
    const handleScroll = () => { if (!deleteOpenRef.current) onClose(); };
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleClose);
      document.removeEventListener("keydown", handleKey);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [handleClose, onClose]);

  const currentIdx = STATUS_OPTIONS.findIndex((s) => s.value === menu.currentStatus);

  const handleStatusChange = async (newStatus: OrdreStatus) => {
    if (newStatus === menu.currentStatus || savingStatus) return;
    setSavingStatus(newStatus);
    try {
      const updated = await ordresApi.update(menu.ordreId, { status: newStatus });
      useOrdreStore.getState().updateOrdre(updated);
      // Reflect the new status on planning appointments linked to this ordre (tooltip chip, etc.)
      usePlanningStore.setState((s) => ({
        appointments: s.appointments.map((a) => (a.ordre_id === menu.ordreId ? { ...a, status: newStatus } : a)),
      }));
      onClose();
    } catch {
      showNotification({ type: "error", message: "Erreur lors du changement de statut" });
      setSavingStatus(null);
    }
  };

  return createPortal(
    <>
    <div
      ref={ref}
      data-planning-context-menu
      className="bg-white rounded-xl shadow-lg border border-neutral-200"
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        zIndex: 10001, // above BaseModal (z-[10000])
        minWidth: 180,
        paddingTop: 6,
        paddingBottom: 6,
        pointerEvents: savingStatus ? "none" : "auto",
      }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div
        style={{
          padding: "2px 12px 6px",
          fontFamily: "Poppins",
          fontSize: 11,
          fontWeight: 600,
          color: "#525252",
        }}
      >
        Changer le statut
      </div>
      {STATUS_GROUPS.map((group, gi) => (
        <div key={group.label}>
          {gi > 0 && <div style={{ height: 1, background: "#F0F0F0", margin: "3px 12px" }} />}
          <div
            style={{
              padding: "3px 12px 2px",
              fontFamily: "Poppins",
              fontSize: 10,
              fontWeight: 500,
              color: "#C0C0C0",
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            {group.label}
          </div>
          {group.statuses.map((sv) => {
            const idx = STATUS_OPTIONS.findIndex((s) => s.value === sv);
            const isCurrent = sv === menu.currentStatus;
            const isSaving = savingStatus === sv;
            const isPast = idx < currentIdx;
            const chip = STATUS_CHIP_STYLES[sv] || { label: sv, bg: "#F4F4F4", color: "#A1A1A1" };
            return (
              <button
                key={sv}
                className={`flex flex-row items-center w-full text-left${isCurrent ? " cursor-default" : " hover:bg-neutral-50 transition-colors cursor-pointer"}`}
                style={{ padding: "3px 12px 3px 20px" }}
                onClick={isCurrent ? undefined : () => handleStatusChange(sv as OrdreStatus)}
                disabled={isCurrent || !!savingStatus}
              >
                <span
                  style={{
                    width: 9,
                    height: 9,
                    borderRadius: 9999,
                    background: getStatusColor(sv as OrdreStatus),
                    flexShrink: 0,
                    marginRight: 8,
                    opacity: isPast ? 0.45 : 1,
                  }}
                />
                {isSaving ? (
                  <svg width={10} height={10} viewBox="0 0 10 10" className="animate-spin" style={{ marginRight: 6, flexShrink: 0 }}>
                    <circle cx="5" cy="5" r="4" fill="none" stroke="#A1A1A1" strokeWidth={1.5} strokeDasharray="18" strokeDashoffset="6" strokeLinecap="round" />
                  </svg>
                ) : isCurrent ? (
                  <svg width={10} height={10} viewBox="0 0 10 10" fill="none" style={{ marginRight: 6, flexShrink: 0 }}>
                    <path d="M2 5l2.5 2.5L8 3" stroke="#1A1A1A" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : null}
                <span
                  className="whitespace-nowrap"
                  style={{
                    fontSize: 12,
                    fontWeight: isCurrent ? 600 : isSaving ? 600 : 500,
                    color: isSaving ? "#525252" : isCurrent ? "#1A1A1A" : isPast ? "#C0C0C0" : "#666666",
                    fontFamily: "Poppins",
                    marginLeft: isCurrent || isSaving ? 0 : 16,
                  }}
                >
                  {chip.label}
                </span>
              </button>
            );
          })}
        </div>
      ))}

      {canDelete && (
        <>
          <div style={{ height: 1, background: "#F0F0F0", margin: "6px 12px 4px" }} />
          <button
            className="flex flex-row items-center w-full text-left hover:bg-red-50 transition-colors cursor-pointer"
            style={{ padding: "5px 12px" }}
            onClick={() => setShowDeleteConfirm(true)}
          >
            <Icon1 name="delete" size={13} color="#EF4444" />
            <span
              className="whitespace-nowrap"
              style={{ fontSize: 12, fontWeight: 500, color: "#EF4444", fontFamily: "Poppins", marginLeft: 8 }}
            >
              Supprimer l'ordre
            </span>
          </button>
        </>
      )}
    </div>

    {showDeleteConfirm && (
      <ConfirmationModal
        title={`Supprimer l'ordre ${menu.num || ""}`.trim() + " ?"}
        message="Cette action est irréversible. L'ordre et ses rendez-vous associés seront définitivement supprimés."
        confirmText="CONFIRMER"
        confirmLabel={deleting ? "Suppression..." : "Oui, supprimer"}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
      />
    )}
    </>,
    document.body,
  );
}
